<template>
  <div class="puppetron make-browser-talk">
    <div class="page-header">
      <router-link to="/br-guides" class="back-link">← Back</router-link>
      <h1>Make Your Browser Talk</h1>
      <p class="page-desc">Convert text or web page content into speech using Workers AI TTS.</p>
    </div>

    <!-- Friendly status / errors (aria-live) -->
    <div aria-live="polite" v-if="lastError" class="status-message error">
      {{ lastError }}
    </div>

    <form id="formtron" @submit.prevent="onSubmit" class="talk-form">
      <div class="mode-row">
        <label class="mode-label"><input type="radio" v-model="mode" value="text" /> Text</label>
        <label class="mode-label"><input type="radio" v-model="mode" value="selector" /> URL + Selector</label>
      </div>

      <!-- Text mode -->
      <div v-if="mode === 'text'" class="card card-body">
        <label class="label">Enter text for the browser to speak</label>
        <textarea
          v-model="text"
          placeholder="Type something for the browser to say..."
          rows="6"
          @input="onTextInput"
          class="text-input"
        ></textarea>
        <div class="hint muted">{{ text.length }} / {{ MAX_CHARS }} chars</div>
      </div>

      <!-- Selector mode -->
      <div v-else class="card card-body selector-mode">
        <label class="label">Target URL</label>
        <input type="url" v-model="url" placeholder="https://example.com" class="text-input" />
        <label class="label" style="margin-top:0.5rem">CSS selector to extract (single)</label>
        <input type="text" v-model="selector" placeholder="e.g. h1, .article .title" class="text-input" />
        <p class="hint">Worker will call BR scrape with this selector and turn the extracted text into speech.</p>

        <div v-if="extractedText || rawBrJson" class="extracted-preview card card-body preview-box">
          <strong>Extracted text (preview):</strong>
          <p v-if="extractedText" class="extracted-text">{{ extractedText }}</p>
          <p v-else class="muted">(no text extracted)</p>

          <div class="preview-actions">
            <button type="button" @click="useExtractedText" :disabled="!extractedText" class="btn small">Use as text</button>
            <button type="button" @click="showRawBrJson = !showRawBrJson" :disabled="!rawBrJson" class="btn small muted-btn">
              {{ showRawBrJson ? 'Hide raw BR JSON' : 'Show raw BR JSON' }}
            </button>
            <button type="button" @click="copyRawBrJson" :disabled="!rawBrJson" class="btn small">Copy raw JSON</button>
            <button type="button" @click="downloadRawBrJson" :disabled="!rawBrJson" class="btn small">Download JSON</button>
          </div>

          <div v-if="showRawBrJson" class="raw-json-wrapper">
            <div class="raw-json-toolbar">
              <span class="badge">Raw BR JSON</span>
              <small class="muted"> — debug view</small>
            </div>
            <pre class="raw-json-pre" v-text="rawBrJsonPreview"></pre>
          </div>
        </div>
      </div>

      <div class="controls-row">
        <div class="action-buttons">
          <button
            type="button"
            @click="requestTts"
            :disabled="!canRequest || isLoading"
            class="btn primary"
            aria-label="Generate and play audio"
          >
            {{ isLoading ? 'Generating...' : 'Generate & Play' }}
          </button>

          <button type="button" @click="pauseAudio" :disabled="!isPlaying || isPaused" class="btn">⏸ Pause</button>
          <button type="button" @click="resumeAudio" :disabled="!isPaused" class="btn">▶ Resume</button>
          <button type="button" @click="stopAudio" :disabled="!isPlaying && !isPaused" class="btn">■ Stop</button>

          <button type="button" @click="downloadAudio" :disabled="!audioUrl" class="btn">⬇ Download</button>
        </div>
      </div>

      <div class="visual-row">
        <div class="visualizer" :class="{ playing: isPlaying || isLoading }" aria-hidden="true">
          <div v-for="n in 12" :key="n" class="bar" :style="barStyle(n)"></div>
        </div>

        <div class="spoken-preview" v-if="mode === 'text' && words.length">
          <span v-for="(w, idx) in words" :key="idx" :class="{ highlight: idx === currentWordIndex }">
            {{ w }}
          </span>
        </div>
      </div>

      <!-- START: Collapsible card for logic diagram -->
      <div id="accordionExample" aria-multiselectable="false" style="width:90%; max-width:900px; margin-top:1rem;">
        <b-card no-body class="mb-2">
          <b-card-header
            header-tag="header"
            class="p-0"
            role="tab"
            id="headingDiagram"
          >
            <h2 class="mb-0">
              <b-button
                v-b-toggle.collapseDiagram
                variant="link"
                block
                class="text-left"
              >
                MakeBrowserTalk 实现逻辑图
              </b-button>
            </h2>
          </b-card-header>

          <b-collapse
            id="collapseDiagram"
            accordion="accordionExample"
            class="border-top"
            role="tabpanel"
            aria-labelledby="headingDiagram"
            v-model="collapseDiagramOpen"
          >
            <b-card-body>
              <!-- 简短说明 -->
              <p class="muted" style="margin-bottom:0.5rem">
                Pages/UI 输入 → Worker (BR/TTS) → R2 存储 → UI 播放
              </p>

              <!-- 在这里插入图片：图片使用 data 中的 diagramUrl（懒加载） -->
              <div class="diagram-wrapper mt-3" v-if="collapseDiagramOpen">
                <div v-if="diagramUrl">
                  <img
                    :src="diagramUrl"
                    :alt="imageAlt"
                    class="img-fluid diagram-img"
                  />
                </div>
                <div v-else class="muted" style="font-size:13px; color:#567;">Loading diagram...</div>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
      <!-- END: Collapsible card for logic diagram -->

    </form>

    <footer class="page-footer">
      Powered by Cloudflare Workers AI · Text-to-Speech
    </footer>
  </div>
</template>

<script>
export default {
  name: "MakeBrowserTalk",

  data() {
    return {
      mode: "text",
      text: "",
      url: "",
      selector: "",
      isLoading: false,
      isPlaying: false,
      isPaused: false,
      audioKey: null,
      audioUrl: null,
      audioEl: null,
      words: [],
      currentWordIndex: -1,
      highlightTimer: null,
      seed: Math.random(),
      extractedText: null,
      lastError: null,
      ttsAbortController: null,
      rawBrJson: null,
      showRawBrJson: false,
      // image/diagram related
      diagramUrl: null,
      imageAlt: 'MakeBrowserTalk 流程图',
      collapseDiagramOpen: false,
      // existing collapse toggle kept for other UI (if needed)
      collapseThreeOpen: false,
    };
  },

  computed: {
    canRequest() {
      if (this.mode === "text") {
        return this.text.trim().length > 0;
      }
      return this.mode === "selector" && this.url.trim().length > 0 && this.selector.trim().length > 0;
    },

    rawBrJsonPreview() {
      if (!this.rawBrJson) return "";
      if (typeof this.rawBrJson === "string") {
        try {
          const parsed = JSON.parse(this.rawBrJson);
          return JSON.stringify(parsed, null, 2);
        } catch (e) {
          return this.rawBrJson;
        }
      } else {
        try {
          return JSON.stringify(this.rawBrJson, null, 2);
        } catch (e) {
          return String(this.rawBrJson);
        }
      }
    }
  },

  // keep parity with backend
  MAX_CHARS: 8000,

  mounted() {
    this.audioEl = document.createElement("audio");
    this.audioEl.preload = "auto";
    try { this.audioEl.crossOrigin = "anonymous"; } catch (e) { console.debug('crossOrigin not supported:', e); }
    this.audioEl.onended = this.onAudioEnded;
    this.audioEl.onpause = () => { this.isPaused = true; };
    this.audioEl.onplay = () => { this.isPaused = false; };
    this.audioEl.onloadedmetadata = this.onAudioMetadata;
    this.audioEl.style.display = "none";
    try { document.body.appendChild(this.audioEl); } catch (e) { console.debug('append audioEl failed:', e); }
    this.onTextInput();
  },

  beforeUnmount() {
    this.cleanupAudio();
  },

  watch: {
    // lazy-load diagram image only when user opens the collapse
    collapseDiagramOpen(newVal) {
      if (newVal && !this.diagramUrl) {
        try {
          // require the diagram asset lazily to avoid bundling cost on initial load.
          // replace the path with your actual asset path; fallback to null on failure.
          this.diagramUrl = require('@/assets/talk-worker.drawio.png');
        } catch (e) {
          console.debug('lazy load diagram failed', e);
          this.diagramUrl = null;
        }
      }
    },

    // keep existing watcher in case you use other collapses
    collapseThreeOpen(newVal) {
      if (newVal && !this.imageUrl) {
        try {
          this.imageUrl = require('@/assets/brTalkFlow.drawio.png');
        } catch (e) {
          console.debug('lazy load image failed', e);
          this.imageUrl = null;
        }
      }
    }
  },

  methods: {
    onTextInput() {
      this.words = this.text.trim().length ? this.text.split(/\s+/) : [];
      this.currentWordIndex = -1;
    },

    // Robust URL builder: use VUE_APP_API_BASE (no trailing /api) and avoid duplicate /api prefix
    buildApiUrl(path) {
      const baseEnv = (process.env.VUE_APP_API_BASE || '').trim();
      const base = baseEnv.replace(/\/+$/, '');
      let p = path || '';
      if (!p.startsWith('/')) p = '/' + p;
      if (!base) return p;
      if (base.toLowerCase().endsWith('/api') && p.toLowerCase().startsWith('/api')) {
        p = p.replace(/^\/api/i, '');
      }
      return base + p;
    },

    // main entry: request TTS (POST to /api/talk)
    async requestTts() {
      if (!this.canRequest || this.isLoading) return;
      this.isLoading = true;
      this.lastError = null;
      this.stopAudio();

      try { if (this.ttsAbortController) this.ttsAbortController.abort(); } catch (e) { /* noop */ }
      this.ttsAbortController = new AbortController();

      const payload = {};
      if (this.mode === "text") payload.text = this.text;
      else { payload.url = this.url; payload.selector = this.selector; }
      payload.format = "mp3";

      // IMPORTANT: call the worker's /api/talk endpoint (worker expects /api/* in this deploy)
      const endpoint = this.buildApiUrl('/api/talk');
      console.log('[MakeBrowserTalk] POST endpoint:', endpoint);

      try {
        const resp = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: this.ttsAbortController.signal,
        });

        if (!resp.ok) {
          const txt = await resp.text().catch(() => "");
          throw new Error(`Server ${resp.status}: ${txt}`);
        }

        const j = await resp.json();
        if (!j.key) throw new Error("No key returned from server");
        this.audioKey = j.key;

        if (j.extractedText) {
          this.rawBrJson = j.extractedText;
          const cleaned = this.tryParseAndExtract(j.extractedText);
          this.extractedText = cleaned || (typeof j.extractedText === 'string' ? j.extractedText : JSON.stringify(j.extractedText));
        } else {
          this.extractedText = null;
          this.rawBrJson = null;
        }

        await this.playKey(j.key);
      } catch (err) {
        console.error("TTS request failed", err);
        this.lastError = "TTS request failed: " + (err.message || err);
        alert(this.lastError);
      } finally {
        this.isLoading = false;
        this.ttsAbortController = null;
      }
    },

    tryParseAndExtract(raw) {
      try {
        if (!raw) return "";
        let parsed = raw;
        if (typeof raw === "string") {
          try { parsed = JSON.parse(raw); } catch (e) {
            const s = raw.trim();
            if (s.startsWith("{") || s.startsWith("[")) return raw;
            return this.stripHtml(s);
          }
        }
        if (!parsed) return "";
        if (Array.isArray(parsed.result) && parsed.result.length) {
          for (const item of parsed.result) {
            if (Array.isArray(item.results) && item.results.length) {
              const first = item.results[0];
              if (first) {
                if (first.text && String(first.text).trim()) return String(first.text).trim();
                if (first.html && String(first.html).trim()) return this.stripHtml(String(first.html));
              }
            }
            if (item.text && String(item.text).trim()) return String(item.text).trim();
            if (item.html && String(item.html).trim()) return this.stripHtml(String(item.html));
          }
        }
        if (typeof parsed.result === "object" && parsed.result !== null) {
          if (parsed.result.text && String(parsed.result.text).trim()) return String(parsed.result.text).trim();
          if (parsed.result.html && String(parsed.result.html).trim()) return this.stripHtml(String(parsed.result.html));
          if (Array.isArray(parsed.result.results) && parsed.result.results.length) {
            const r0 = parsed.result.results[0];
            if (r0) {
              if (r0.text && String(r0.text).trim()) return String(r0.text).trim();
              if (r0.html && String(r0.html).trim()) return this.stripHtml(String(r0.html));
            }
          }
        }
        if (typeof parsed.result === "string") {
          const s = parsed.result.trim();
          return this.stripHtml(s);
        }
        if (parsed.text && String(parsed.text).trim()) return String(parsed.text).trim();
        if (parsed.html && String(parsed.html).trim()) return this.stripHtml(String(parsed.html));
        if (parsed.content && String(parsed.content).trim()) return this.stripHtml(String(parsed.content));
        try { return JSON.stringify(parsed); } catch (e) { return String(parsed); }
      } catch (e) {
        console.debug("tryParseAndExtract failed:", e);
        return "";
      }
    },

    stripHtml(html) {
      if (!html) return "";
      return String(html)
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
        .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
        .replace(/<\/?[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },

    // play audio by R2 key - fetch blob -> createObjectURL
    async playKey(key) {
      // IMPORTANT: audio endpoint uses /api/audio/:key
      const audioEndpoint = this.buildApiUrl(`/api/audio/${encodeURIComponent(key)}`);
      console.log('[MakeBrowserTalk] GET audio endpoint:', audioEndpoint);

      try {
        if (this.audioUrl && this.audioUrl.startsWith("blob:")) {
          try { URL.revokeObjectURL(this.audioUrl); } catch (e) { /* noop */ }
        }

        const resp = await fetch(audioEndpoint);
        if (!resp.ok) {
          const txt = await resp.text().catch(() => "");
          throw new Error(`Audio fetch failed ${resp.status}: ${txt}`);
        }
        const blob = await resp.blob();
        this.audioUrl = URL.createObjectURL(blob);
        this.audioEl.src = this.audioUrl;
        await this.audioEl.play();
        this.isPlaying = true;
        this.isPaused = false;
        if (this.mode === "text") {
          this.words = this.text.trim().length ? this.text.split(/\s+/) : [];
          this.currentWordIndex = -1;
        } else {
          this.words = [];
          this.currentWordIndex = -1;
        }
      } catch (err) {
        console.error("play failed", err);
        this.lastError = "Audio play failed: " + (err.message || err);
        alert("Audio play failed: " + (err.message || err));
        this.isPlaying = false;
        this.isPaused = false;
      }
    },

    onAudioMetadata() {
      if (!this.audioEl || !this.audioEl.duration || this.mode !== "text" || this.words.length === 0) return;
      this.clearHighlightTimer();
      const totalMs = Math.max(1000, Math.floor(this.audioEl.duration * 1000));
      const stepMs = Math.max(120, Math.floor(totalMs / this.words.length));
      this.highlightTimer = setInterval(() => {
        if (!this.isPlaying || this.isPaused) return;
        if (this.currentWordIndex < this.words.length - 1) {
          this.currentWordIndex++;
        }
      }, stepMs);
    },

    pauseAudio() { try { this.audioEl.pause(); this.isPaused = true; } catch (e) { console.debug(e); } },
    resumeAudio() { try { this.audioEl.play(); this.isPaused = false; } catch (e) { console.debug(e); } },
    stopAudio() { try { this.audioEl.pause(); this.audioEl.currentTime = 0; } catch (e) { console.debug(e); } this.isPlaying = false; this.isPaused = false; this.clearHighlightTimer(); this.currentWordIndex = -1; },
    onAudioEnded() { this.isPlaying = false; this.isPaused = false; this.clearHighlightTimer(); this.currentWordIndex = -1; },
    clearHighlightTimer() { if (this.highlightTimer) { clearInterval(this.highlightTimer); this.highlightTimer = null; } },

    cleanupAudio() {
      if (this.audioEl) {
        this.audioEl.onended = null;
        this.audioEl.onpause = null;
        this.audioEl.onplay = null;
        this.audioEl.onloadedmetadata = null;
        try { this.audioEl.pause(); } catch (err) { console.debug(err); }
        try { if (this.audioEl.parentNode) this.audioEl.parentNode.removeChild(this.audioEl); } catch (e) { /* noop */ }
        this.audioEl = null;
      }
      if (this.audioUrl && this.audioUrl.startsWith('blob:')) {
        try { URL.revokeObjectURL(this.audioUrl); } catch (e) { /* noop */ }
      }
      this.audioUrl = null;
      this.clearHighlightTimer();
    },

    downloadAudio() {
      if (!this.audioUrl) return;
      try {
        const a = document.createElement('a');
        a.href = this.audioUrl;
        a.download = `${this.audioKey || 'audio'}.mp3`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) {
        console.error("download failed", e);
        alert("Download failed: " + (e.message || e));
      }
    },

    useExtractedText() {
      if (!this.extractedText) return;
      const playNow = confirm("Insert extracted text into the textarea and play the generated audio now?");
      this.mode = "text";
      this.text = this.extractedText;
      this.onTextInput();
      if (playNow) {
        if (this.audioKey) { this.playKey(this.audioKey).catch(e => console.debug(e)); }
        else { this.requestTts(); }
      }
    },

    async copyRawBrJson() {
      if (!this.rawBrJson) return;
      try {
        const textToCopy = (typeof this.rawBrJson === 'string') ? this.rawBrJson : JSON.stringify(this.rawBrJson);
        await navigator.clipboard.writeText(textToCopy);
        alert('Raw BR JSON copied to clipboard');
      } catch (e) {
        console.error('copyRawBrJson failed', e);
        alert('Copy failed: ' + (e.message || e));
      }
    },

    downloadRawBrJson() {
      if (!this.rawBrJson) return;
      try {
        const textToSave = (typeof this.rawBrJson === 'string') ? this.rawBrJson : JSON.stringify(this.rawBrJson, null, 2);
        const blob = new Blob([textToSave], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `raw-br-${this.audioKey || 'extracted'}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ } }, 5000);
      } catch (e) { console.error('downloadRawBrJson failed', e); alert('Download failed: ' + (e.message || e)); }
    },

    barStyle(n) {
      const base = 6 + ((n + Math.floor(this.seed * 10)) % 8) * 2;
      return { height: base + "px" };
    },

    onSubmit() { this.requestTts(); }
  }
};
</script>

<style scoped>
.puppetron {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Page header */
.page-header { margin-bottom: 1.5rem; }
.back-link {
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color 0.15s;
}
.back-link:hover { color: #F6821F; }
h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #F6821F;
  letter-spacing: -0.03em;
  margin: 0 0 0.4rem;
}
.page-desc { font-size: 14px; color: #6B7280; margin: 0; line-height: 1.5; }

/* talk form card */
.talk-form {
  background: #FFFFFF;
  border: 1px solid #E4E4E0;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

/* mode row */
.mode-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
}
.mode-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
}
.mode-label input[type='radio'] { accent-color: #F6821F; cursor: pointer; }

/* card for mode content */
.card.card-body {
  background: #FAFAFA;
  border: 1px solid #E4E4E0;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: none;
  text-align: left;
}

/* inputs */
.text-input, textarea {
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #D4D4D0;
  border-radius: 8px;
  background: #FFFFFF;
  font-size: 14px;
  font-family: inherit;
  color: #1D1D1D;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.text-input:focus, textarea:focus {
  outline: none;
  border-color: #F6821F;
  box-shadow: 0 0 0 3px rgba(246,130,31,0.15);
}
.text-input::placeholder, textarea::placeholder { color: #9CA3AF; }

/* label */
.label { font-size: 13px; font-weight: 500; color: #374151; margin-bottom: 0.35rem; display: block; }
.hint { font-size: 12px; color: #9CA3AF; margin-top: 0.3rem; }
.muted { color: #9CA3AF; }

/* controls */
.controls-row {
  display: flex;
  justify-content: flex-start;
  margin-top: 1rem;
}
.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* buttons */
.btn {
  border: 1px solid #E4E4E0;
  background: #FFFFFF;
  color: #374151;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.15s;
  white-space: nowrap;
}
.btn:hover { border-color: #F6821F; color: #F6821F; background: #FFF8F3; }
.btn:disabled { opacity: 0.4; cursor: not-allowed; pointer-events: none; }
.btn.primary {
  background: #F6821F;
  border-color: #F6821F;
  color: #FFFFFF;
  box-shadow: 0 2px 8px rgba(246,130,31,0.25);
}
.btn.primary:hover { background: #E07315; border-color: #E07315; color: #FFFFFF; }
.btn.small { font-size: 12px; padding: 0.35rem 0.7rem; }
.muted-btn { background: #F5F4F2; border-color: #E4E4E0; color: #6B7280; }
.muted-btn:hover { border-color: #D1D1CE; color: #374151; background: #EBEBEA; }

/* visualizer */
.visual-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 1rem;
}
.visualizer {
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 40px;
  margin-bottom: 0.5rem;
  transition: opacity 0.2s ease;
  opacity: 0.2;
}
.visualizer.playing { opacity: 1; }
.visualizer .bar {
  width: 4px;
  background: linear-gradient(180deg, #FBAD41, #F6821F);
  border-radius: 2px;
  transform-origin: bottom center;
  animation-name: pulse;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;
  animation-duration: 700ms;
}
@keyframes pulse {
  0% { transform: scaleY(0.2); opacity: 0.6; }
  50% { transform: scaleY(1.4); opacity: 1; }
  100% { transform: scaleY(0.2); opacity: 0.6; }
}

/* spoken preview */
.spoken-preview {
  width: 100%;
  padding: 0.6rem 0.8rem;
  background: #FAFAFA;
  border: 1px solid #E4E4E0;
  border-radius: 8px;
  text-align: left;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  overflow-wrap: break-word;
}
.spoken-preview span.highlight {
  background: rgba(246,130,31,0.15);
  border-radius: 3px;
  padding: 0.05rem 0.18rem;
  color: #C05A00;
}

/* preview box for selector mode */
.preview-box { margin-top: 0.75rem; }
.extracted-text { font-size: 14px; color: #374151; margin: 0.5rem 0 0; line-height: 1.5; }
.preview-actions { margin-top: 0.6rem; display: flex; gap: 0.4rem; flex-wrap: wrap; }

/* raw JSON viewer */
.raw-json-wrapper {
  margin-top: 0.6rem;
  border-radius: 8px;
  background: #F5F4F2;
  border: 1px solid #E4E4E0;
  padding: 0.5rem;
}
.raw-json-pre {
  font-family: 'ui-monospace', SFMono-Regular, Menlo, Monaco, monospace;
  font-size: 12px;
  color: #374151;
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 0.4rem;
  max-height: 260px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}
.raw-json-toolbar { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.3rem; }
.badge {
  background: #FFF3E8;
  color: #F6821F;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 11px;
  border: 1px solid rgba(246,130,31,0.2);
}

/* diagram */
.diagram-wrapper { display: flex; justify-content: center; margin-top: 0.75rem; }
.diagram-img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08);
  border: 1px solid #E4E4E0;
}

/* status / errors */
.status-message {
  margin: 0.75rem 0;
  font-size: 14px;
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  background: #FFF3E8;
  border: 1px solid rgba(246,130,31,0.2);
  color: #6B7280;
}
.status-message.error { background: #FEF2F2; border-color: #FECACA; color: #DC2626; }

/* selector mode card */
.selector-mode { display: flex; flex-direction: column; gap: 0.5rem; }

/* footer */
.page-footer {
  font-size: 12px;
  color: #9CA3AF;
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #E4E4E0;
}

@media (max-width: 600px) {
  h1 { font-size: 1.6rem; }
  .action-buttons { flex-direction: column; }
  .btn { width: 100%; }
}
</style>